<template>
  <section
    class="kanban-column"
    :class="`kanban-column--${column.id}`"
    data-component="Kanban/KanbanColumn"
  >
    <!-- Column header -->
    <header class="kanban-column__header" :style="{ '--column-color': column.color, '--column-color-light': column.colorLight }">
      <div class="kanban-column__header-left">
        <i :class="`${column.icon} kanban-column__icon`"></i>
        <span class="kanban-column__title">{{ column.label }}</span>
      </div>
      <span class="kanban-column__count">{{ column.cards.length }}</span>
    </header>

    <!-- Cards list (scrollable, height-constrained) -->
    <div class="kanban-column__body" ref="bodyRef">
      <TransitionGroup name="kanban-card-list" tag="div" class="kanban-column__cards">
        <KanbanCard
          v-for="visit in visibleCards"
          :key="visit.id"
          :visit="visit"
          @card-click="$emit('card-click', $event)"
        />
      </TransitionGroup>

      <!-- Sentinel (IntersectionObserver target) -->
      <div ref="sentinelRef" class="kanban-column__sentinel" aria-hidden="true"></div>

      <!-- Loading more indicator -->
      <div v-if="isLoadingMore" class="kanban-column__loading-more">
        <i class="fas fa-circle-notch fa-spin"></i>
      </div>

      <!-- Empty state -->
      <div v-if="column.cards.length === 0" class="kanban-column__empty">
        <i class="fas fa-inbox kanban-column__empty-icon"></i>
        <span>Aucune visite</span>
      </div>
    </div>
  </section>
</template>

<script>
import KanbanCard from './KanbanCard.vue'

const PAGE_SIZE = 20

export default {
  name: 'KanbanColumn',
  components: { KanbanCard },
  props: {
    column: {
      type: Object,
      required: true
      // column shape: { id, label, color, colorLight, icon, cards[] }
    }
  },
  emits: ['card-click'],
  data() {
    return {
      visibleCount:  PAGE_SIZE,
      isLoadingMore: false,
      observer:      null
    }
  },
  computed: {
    visibleCards() {
      return this.column.cards.slice(0, this.visibleCount)
    },
    hasMore() {
      return this.visibleCount < this.column.cards.length
    }
  },
  watch: {
    // Reset visible count when cards change (new filter applied)
    'column.cards'() {
      this.visibleCount = PAGE_SIZE
    }
  },
  mounted() {
    this.initObserver()
  },
  beforeUnmount() {
    this.destroyObserver()
  },
  methods: {
    initObserver() {
      if (!('IntersectionObserver' in window)) return
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (entry.isIntersecting && this.hasMore && !this.isLoadingMore) {
            this.loadMore()
          }
        },
        {
          root: this.$refs.bodyRef,
          rootMargin: '0px 0px 40px 0px',
          threshold: 0.1
        }
      )
      if (this.$refs.sentinelRef) {
        this.observer.observe(this.$refs.sentinelRef)
      }
    },
    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    loadMore() {
      this.isLoadingMore = true
      // Micro-delay for smooth UX (gives the browser time to paint)
      setTimeout(() => {
        this.visibleCount = Math.min(
          this.visibleCount + PAGE_SIZE,
          this.column.cards.length
        )
        this.isLoadingMore = false
      }, 120)
    }
  }
}
</script>

<style lang="scss" scoped>
@use './KanbanColumn.scss';
</style>
